import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addSlider, editSlider } from '../../../../api/endpoints/cmsSite';
import type { Slider } from '../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';

const schema = z.object({
  imgPath: z.string().min(1, 'Görsel yolu gerekli'),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { imgPath: '' };

function toFormValues(slider: Slider): FormValues {
  return { imgPath: slider.imgPath ?? '' };
}

/** Create/edit dialog for Slider. Confirmed via local-backend swagger + live curl (see
 *  api/types/cmsSite.ts header) — the smallest of the 7 CMS-Site resources. Always rendered
 *  inside a specific site's tab, so `site_Id` is injected from the `siteId` prop. */
export function SliderFormDialog({
  open,
  slider,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  slider: Slider | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!slider;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(slider ? toFormValues(slider) : emptyValues);
  }, [open, slider, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && slider
        ? editSlider({ id: slider.id, site_Id: siteId, ...values })
        : addSlider({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Slider güncellendi.' : 'Slider oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'sliders'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Slider'ı Güncelle" : 'Yeni Slider'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Görsel Yolu"
                error={!!errors.imgPath}
                helperText={errors.imgPath?.message}
                {...register('imgPath')}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
